import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiV2 from '../../../services/apiV2';

type Offer = { id: number; mode: string; release: string; available: boolean; href: string | null; options: { plan_id: number; title: string }[] };

// Remount on class changes so another class's choices never flash or receive a click.
export function ActivityChoices({ sectionId }: { sectionId: number }) {
  return <ClassChoices key={sectionId} sectionId={sectionId} />;
}

function ClassChoices({ sectionId }: { sectionId: number }) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const [selection, setSelection] = useState<{ offer: number; plan: number } | null>(null);
  const navigate = useNavigate();
  const mounted = React.useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const base = `/activity-offers/my/sections/${sectionId}/`;
  useEffect(() => {
    let current = true;
    const refresh = async () => {
      try {
        const { data } = await apiV2.get(base);
        if (current) { setOffers(data.offers || []); setError(''); }
      } catch { if (current) setError('Could not refresh your optional activities. Your required work is still available.'); }
    };
    void refresh();
    const timer = window.setInterval(refresh, 10000);
    return () => { current = false; window.clearInterval(timer); };
  }, [base, retry]);
  const choose = async () => {
    if (!selection || busy) return;
    setBusy(true);
    try {
      const { data } = await apiV2.post(base, { offer_id: selection.offer, plan_id: selection.plan });
      if (mounted.current) navigate(data.href);
    } catch (e: any) {
      if (mounted.current) setError(e?.response?.data?.detail || 'Could not open your choice. Try again; the same choice will not create duplicate work.');
    } finally { if (mounted.current) setBusy(false); }
  };
  if (!offers.length && !error) return null;
  return <section className="rounded-xl border border-border bg-surface p-5 space-y-4" aria-label="Optional activities">
    <div><h2 className="text-xl font-semibold">Ready for something else?</h2><p className="text-sm text-muted">Optional activities from your teacher. These do not replace your required work or exit ticket.</p></div>
    {error && <div role="alert"><p>{error}</p><button className="text-brand underline" onClick={() => setRetry(v => v + 1)}>Refresh choices</button></div>}
    {offers.map(offer => <div key={offer.id} className="border-t border-border pt-4 space-y-3">
      {offer.href ? <Link className="btn-secondary inline-block" to={offer.href}>Continue chosen activity</Link> : !offer.available ? <p className="text-muted">{offer.release === 'approved' ? 'Waiting for your teacher’s approval.' : 'Finish the required activity to unlock this choice.'}</p> : <>
        <p>{offer.mode === 'choice' ? 'Choose one — you do not need to do them all.' : 'An extra activity is available.'}</p>
        <div className="flex flex-wrap gap-3">{offer.options.map(option => <button key={option.plan_id} className={selection?.offer === offer.id && selection.plan === option.plan_id ? 'btn-primary' : 'btn-secondary'} aria-pressed={selection?.offer === offer.id && selection.plan === option.plan_id} disabled={busy || !!error} onClick={() => setSelection({ offer: offer.id, plan: option.plan_id })}>{option.title}</button>)}</div>
        {selection?.offer === offer.id && <button className="btn-primary" disabled={busy || !!error} onClick={() => void choose()}>{busy ? 'Opening…' : 'Start my choice'}</button>}
      </>}
    </div>)}
  </section>;
}
